import { Component, ChangeDetectionStrategy, signal, HostListener, OnInit, OnDestroy, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { GlobalHeaderComponent } from '../../../../shared/components/layout/global-header/global-header';
import { GlobalFooterComponent } from '../../../../shared/components/layout/global-footer/global-footer';
import { GlobalSidebarComponent } from '../../../../shared/components/layout/global-sidebar/global-sidebar';
import { SidebarItem } from '../../../../shared/components/layout/global-sidebar/sidebar.model';
import { resolveMenuPermissionsFromRoute, routeChainHidesSidebar } from '../../../../shared/components/layout/global-sidebar/sidebar-utils';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { filter, map, startWith, Subscription } from 'rxjs';

@Component({
  selector: 'app-cpf-layout',
  standalone: true,
  imports: [
    GlobalHeaderComponent,
    GlobalFooterComponent,
    GlobalSidebarComponent,
    RouterOutlet,
    MatSidenavModule,
    BreadcrumbComponent,
    TranslatePipe,
  ],
  templateUrl: './cpf-layout.component.html',
  styleUrl: './cpf-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpfLayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private routerSub?: Subscription;
  isMobile = signal(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  hideSidebar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => routeChainHidesSidebar(this.activatedRoute.snapshot)),
      startWith(routeChainHidesSidebar(this.activatedRoute.snapshot)),
    ),
    { requireSync: true },
  );

  menuConfig: SidebarItem[] = [
    { id: 'dash', label: 'cpf.sidebar.dashboard', icon: 'grid_view', route: '/cpf' },
    { id: 'accounts', label: 'cpf.sidebar.accounts', icon: 'people', route: '/cpf/accounts' },
    { id: 'nominee', label: 'cpf.sidebar.nominee', icon: 'people_outline', route: '/cpf/nominee' },
    { id: 'monthly-contributions', label: 'cpf.contribution.tabMonthly', icon: 'show_chart', route: '/cpf/contributions/monthly' },
    // {
    //   id: 'contributions',
    //   label: 'cpf.sidebar.contributions',
    //   icon: 'show_chart',
    //   children: [
    //     { id: 'monthly-contributions', label: 'cpf.contribution.tabMonthly', route: '/cpf/contributions/monthly' },
    //     { id: 'extra-contributions', label: 'cpf.contribution.tabExtra', route: '/cpf/contributions/extra' }
    //   ]
    // },
    { id: 'advance', label: 'cpf.sidebar.advance', icon: 'credit_card', route: '/cpf/advance' },
    { id: 'interest', label: 'cpf.sidebar.interest', icon: 'percent', route: '/cpf/interest' },
    { id: 'retirement', label: 'cpf.sidebar.retirement', icon: 'workspace_premium', route: '/cpf/retirement-settlement' },
    { id: 'death', label: 'cpf.sidebar.death', icon: 'favorite_border', route: '/cpf/death-settlement' },
    { id: 'ledger', label: 'cpf.sidebar.ledger', icon: 'menu_book', route: '/cpf/ledger' },
    { id: 'vouchers', label: 'cpf.sidebar.vouchers', icon: 'receipt_long', route: '/cpf/vouchers' },
    { id: 'receipt-voucher', label: 'cpf.sidebar.receiptVoucher', icon: 'receipt', route: '/cpf/receipt-voucher' },
    { id: 'payment-management', label: 'cpf.sidebar.paymentManagement', icon: 'payments', route: '/cpf/payment-management' },
    { id: 'reports', label: 'cpf.sidebar.reports', icon: 'bar_chart', route: '/cpf/reports' },
    { id: 'audit', label: 'cpf.sidebar.audit', icon: 'verified_user', route: '/cpf/audit-log' },
  ];

  ngOnInit(): void {
    this.resolvePermissionsFromRouter();
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.resolvePermissionsFromRouter();
      if (typeof document !== 'undefined') {
        const mainContent = document.querySelector('.main-content');
        if (mainContent) mainContent.scrollTop = 0;
        const pageContainer = document.querySelector('.page-container');
        if (pageContainer) pageContainer.scrollTop = 0;
      }
    });
  }

  private resolvePermissionsFromRouter(): void {
    const rootRoute = this.router.config.find(r => r.path === 'cpf');
    this.menuConfig = resolveMenuPermissionsFromRoute(this.menuConfig, rootRoute, 'cpf');
  }

  @HostListener('window:resize')
  onResize() {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth <= 768);
      if (this.isMobile()) this.sidenavOpened.set(false);
      else this.sidenavOpened.set(true);
    }
  }
  sidenavOpened = signal(typeof window !== 'undefined' ? window.innerWidth > 768 : true);

  toggleSidenav(): void {
    this.sidenavOpened.update(v => !v);
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }
}
